// tslint:disable:no-redundant-jsdoc
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SyncProcessorService } from '@vending/sync-engine-client';
import { StoredData } from '@vending/sync-engine-client/lib/models/storedData';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { Observable, catchError, firstValueFrom, retry } from 'rxjs';
import { ArticleModel } from 'src/app/models/articles/article';
import { ErrorService } from 'src/packages/mitsBasics';
import { EventsService } from 'src/packages/mitsBasics';
import { FulltextService } from '../fulltext-service/fulltext.service';
import { OfflineDataService } from 'src/packages/mitsBasics';
import { ArticleEanService } from './article-ean.service';

export type ArticleUseType = 'service_article' | 'filling_article';

const FILLING_STORAGE = 'filling_articles';
const SERVICE_STORAGE = 'service_articles';

@Injectable({
  providedIn: 'root',
})
export class ArticleService extends OfflineDataService<ArticleModel> {
  constructor(
    public indexedDBService: NgxIndexedDBService,
    public syncProcessor: SyncProcessorService,
    public http: HttpClient,
    public errorService: ErrorService,
    public events: EventsService,
    public fulltextService: FulltextService,
    private readonly articleEanService: ArticleEanService,
  ) {
    super(
      indexedDBService,
      syncProcessor,
      'Article',
      http,
      errorService,
      events,
      'articles/',
      'article',
      ['created_at', 'updated_at', 'created_by_id', 'unit', 'packaging_unit'],
      ['eans'],
      'ean',
    );
    this.bulkSize = 5;
    this.bulkSyncActive = true;
  }

  /**
   * Validiert die Offlinedaten in jedem Sync
   * @return {boolean} true, wenn validiert werden soll
   */
  override get checkEachSync(): boolean {
    return this.checkAlreadyValidated();
  }

  /**
   * Name des Icons für die Klasse
   * @return {string}
   */
  public get iconName(): string {
    return 'pricetag';
  }

  /**
   * loads article with name 'Fahrzone'
   * @returns geet-request to articles/drive_zone
   */
  filterForDriveZone(): Observable<any> {
    return this.http
      .get<any>(this.endpointWithUrl + 'drive_zone')
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Gibt die ersten 25 Artikel zurück die gefunden werden
   * Falls keine gefunden werden, werden nur die ersten 25 Artikel zurückgegeben (ohne Suche)
   * @param searchText
   * @returns
   */
  public async fastLimitedSearch(searchText: string): Promise<ArticleModel[]> {
    if (searchText?.length < 3) {
      const foundArticles = await this.localAllwithPaging(1);
      return foundArticles.data;
    } else {
      const articles = await firstValueFrom(this.fulltextSearch(searchText));
      return articles;
    }
  }

  /**
   * Wenn der Suchtext kleiner als 3 Zeichen ist werden die ersten 25 Artikel der Nutzungsart zurückgegeben
   * Ansonsten alle Ergebnisse der Fulltextsuche
   * @param searchText
   * @param useType
   * @returns
   */
  public async fastLimitedSearchByUseType(
    searchText: string,
    useType: ArticleUseType,
  ): Promise<ArticleModel[]> {
    if (searchText.length < 3) {
      const searchKey = useType + 's';
      const articles = await this.fulltextService.search(searchKey, this.type);
      return firstValueFrom(articles);
    } else {
      return await this.fullTextSearchByUseType(searchText, useType);
    }
  }

  /**
   * Sucht nach Artikeln über die Volltextsuche
   * - Filterung der Artikel nach Service- und Befüllungsartikel entsprechend der Nutzerrolle (#627)
   * @param input - Der Suchstring
   * @param useType
   */
  public async fullTextSearchByUseType(
    input: string,
    useType: ArticleUseType,
  ): Promise<ArticleModel[]> {
    const articles = await firstValueFrom(this.fulltextSearch(input));
    return articles.filter(a => a[useType]);
  }

  public async listFullText(article?: ArticleModel): Promise<any[]> {
    // TODO nicht implementiert
    return Promise.resolve([]); //this.fulltextService.get(article);
  }

  /**
   * Fulltext neu aufbauen
   */
  public async rebuildFullText(article?: ArticleModel) {
    let articles: StoredData<ArticleModel>[];
    if (!article) articles = await firstValueFrom(this.localAllStoredData());
    else articles = [this.createStoredData(article)];
    for (const article of articles) {
      this.fulltextService.store(article.content, 'name');
      this.fulltextService.store(article.content, 'article_number', true);
      this.addToUseTypeFullText(article);
      this.checkChangesToEans(article.content, {} as ArticleModel);
    }
  }

  protected override async bulkStore(objs: ArticleModel[]): Promise<void> {
    if (objs.length === 0) return;
    if (this.initialSync) await this.bulkStoreFulltextWithEans(objs);
    else {
      const articleIds = objs.map(obj => obj.id);
      const currentObjs = await firstValueFrom(this.bulkFind(articleIds));

      // TODO hier muss noch irgendwie die Verknüpfung zum Type rein, da es sonst nicht klappt

      // Neue Artikel, nicht in der idb einfügen
      const newObjects = objs.filter(
        obj => !currentObjs.find(o => o.id === obj.id),
      );
      await this.bulkStoreFulltextWithEans(newObjects);

      // Artikel, die bereits in der idb sind, updaten
      const updatedObjects = objs.filter(obj =>
        currentObjs.find(o => o.id === obj.id),
      );
      // Bestehende Artikel bearbeiten
      // ------

      const localObjWithNameChanged: ArticleModel[] = [];

      const objWithNameChanged = updatedObjects.filter(obj => {
        const currentObj = currentObjs.find(o => o.id === obj.id);
        const result = currentObj && currentObj.name !== obj.name;
        if (result) localObjWithNameChanged.push(currentObj);
        return result;
      });

      if (objWithNameChanged.length > 0) {
        this.fulltextService.bulkDelete(localObjWithNameChanged, 'name');
        this.fulltextService.bulkStore(objWithNameChanged, 'name');
      }

      // Fulltext Name entfernen für alle noch existierenden Einträge
      this.fulltextService.bulkDelete(objs, 'article_number', true);
      this.fulltextService.bulkDeleteCustom(
        currentObjs.filter(obj => obj.filling_article),
        FILLING_STORAGE,
      );
      this.fulltextService.bulkDeleteCustom(
        currentObjs.filter(obj => obj.service_article),
        SERVICE_STORAGE,
      );

      this.fulltextService.bulkStore(objs, 'article_number', true);
      this.fulltextService.bulkStoreCustom(
        updatedObjects.filter(obj => obj.filling_article),
        FILLING_STORAGE,
      );
      this.fulltextService.bulkStoreCustom(
        updatedObjects.filter(obj => obj.service_article),
        SERVICE_STORAGE,
      );

      for (const obj of updatedObjects) {
        const currentObj = currentObjs.find(o => o.id === obj.id);

        // Kein currentObj = neuer Artikel ohne Vorversion -> nichts zu vergleichen
        if (currentObj) await this.checkChangesToEans(currentObj, obj);
      }
    }
    await super.bulkStore(objs);
  }
  /**
   * Speichert Artikelnamen im fulltextst-Store und EANs im EAN-Store
   * @param obj - der Artikel, der gespeichert werden soll
   */
  private async bulkStoreFulltextWithEans(objs: ArticleModel[]): Promise<void> {
    if (objs.length === 0) return;
    this.fulltextService.bulkStore(objs, 'name');
    this.fulltextService.bulkStore(objs, 'article_number', true);
    this.fulltextService.bulkStoreCustom(
      objs.filter(obj => obj.filling_article),
      FILLING_STORAGE,
    );
    this.fulltextService.bulkStoreCustom(
      objs.filter(obj => obj.service_article),
      SERVICE_STORAGE,
    );
    const eanPromises: Promise<void>[] = [];
    for (const article of objs) {
      for (const ean of article.eans) {
        eanPromises.push(this.articleEanService.storeEan(ean));
      }
    }
    await Promise.all(eanPromises);
  }

  /**
   * Gleicht die lokale EAN-Spiegelung mit den Server-EANs des Artikels ab:
   * server-entfernte Codes werden gelöscht, aktuelle per remote_id gespiegelt.
   * @param currentObj lokal gespeicherter Artikel
   * @param obj synchronisierter Artikel
   */
  private async checkChangesToEans(
    currentObj: ArticleModel,
    obj: ArticleModel,
  ) {
    const incomingCodes = obj.eans.map(ean => ean.remote_id);
    // Vom Server entfernte Codes lokal löschen
    for (const ean of currentObj.eans) {
      if (!incomingCodes.includes(ean.remote_id)) {
        await this.articleEanService.deleteEan(ean);
      }
    }
    for (const ean of obj.eans) {
      await this.articleEanService.storeEan(ean);
    }
  }

  /**
   * Daten aus dem lokalen Datenbestand löschen
   * @param {number} id
   * @return {Promise<any>}
   */
  public async localDelete(id: number): Promise<any> {
    const article = await this.localFind(id);
    if (article) {
      const currentObj = article.content;
      await super.localDelete(id);
      if (currentObj) {
        this.fulltextService.delete(currentObj, 'name');
        this.fulltextService.delete(currentObj, 'article_number', true);
        for (const ean of currentObj.eans) {
          this.articleEanService.deleteEan(ean);
        }
      }
    }
  }

  /**
   * Sucht nach Artikeln anhand eines Wertes (name)
   * @param value - der Wert, nach dem gesucht werden soll
   * @returns
   */
  public fulltextSearch(value: string): Observable<ArticleModel[]> {
    return this.fulltextService.search(value, this.type);
  }

  /**
   * loads article with name 'Arbeitseinheit'
   * @returns geet-request to articles/work_articles
   */
  filterForWork(): Observable<any> {
    return this.http
      .get<any>(this.endpointWithUrl + 'work_articles')
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Lädt alle Artikelgruppen-Namen (inkl. 'Ohne Artikelgruppe') für die Gruppierungs-Auswahl.
   * @returns get-request to articles/group_values
   */
  groupValues(): Observable<string[]> {
    return this.http
      .get<string[]>(this.endpointWithUrl + 'group_values')
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Ruft ein Objekt mit Details ab
   */
  findEAN(ean: string): Observable<any> {
    return this.http
      .get<any>(this.endpointWithUrl + '?ean_code=' + ean)
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  private addToUseTypeFullText(obj: StoredData<ArticleModel>) {
    if (obj.content.filling_article)
      this.fulltextService.storeCustom(obj.content, FILLING_STORAGE);

    if (obj.content.service_article)
      this.fulltextService.storeCustom(obj.content, SERVICE_STORAGE);
  }
}
